import React, { useRef } from 'react';
import { Box } from 'rebass';

// Custom pointer-events trim slider.
//
// Why not react-slider: this lives inside the app modal, whose react-scrolllock wrapper
// preventDefault()s touchmove — that silently kills react-slider's touch dragging on
// mobile. Pointer events + setPointerCapture + `touch-action: none` let us own the drag
// on every device (mouse, touch, pen) regardless of the surrounding scroll handling, and
// give us big, ergonomic handles for a good mobile trim experience.
//
// Value shape: [trimStart, playhead, trimEnd]. Dragging the highlighted window moves the
// selection (keeping its width); dragging/tapping scrubs the playhead within it. onChange
// receives the full [start, playhead, end] array (VideoTrimmer clamps/normalizes it).

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function TrimSlider({ value = [0, 0, 0], min = 0, max = 0, step = 0.016, onChange, onClick }) {
  const trackRef = useRef(null);
  const dragRef = useRef(null);

  const [start = 0, playhead = 0, end = 0] = value || [];
  const hi = Number.isFinite(max) && max > min ? max : min;
  const range = Math.max(0, hi - min);
  const windowSize = Math.max(0, end - start);
  const ready = range > 0;

  const snap = (v) => {
    const clamped = clamp(v, min, hi);
    if (!step) return clamped;
    const snapped = Math.round((clamped - min) / step) * step + min;
    return clamp(Number(snapped.toFixed(3)), min, hi);
  };

  const valueAtX = (clientX) => {
    const el = trackRef.current;
    if (!el || !ready) return min;
    const rect = el.getBoundingClientRect();
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    return snap(min + ratio * range);
  };

  const toPct = (v) => (ready ? ((clamp(v, min, hi) - min) / range) * 100 : 0);

  const emit = (s, p, e) => onChange && onChange([s, p, e]);

  const beginDrag = (mode, e) => {
    if (onClick) onClick(); // pause playback while the user interacts
    dragRef.current = { mode, startX: e.clientX, startVal: start };
    try { trackRef.current && trackRef.current.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
  };

  // Tap / drag on the bare timeline scrubs the playhead (kept inside the window).
  const onTrackPointerDown = (e) => {
    if (!ready) return;
    beginDrag('playhead', e);
    emit(start, clamp(valueAtX(e.clientX), start, end), end);
  };

  const onWindowPointerDown = (e) => {
    if (!ready) return;
    e.stopPropagation();
    beginDrag('window', e);
  };

  const onPlayheadPointerDown = (e) => {
    if (!ready) return;
    e.stopPropagation();
    beginDrag('playhead', e);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    const el = trackRef.current;
    if (!drag || !el || !ready) return;
    if (drag.mode === 'playhead') {
      emit(start, clamp(valueAtX(e.clientX), start, end), end);
    } else {
      const rect = el.getBoundingClientRect();
      const deltaRatio = rect.width > 0 ? (e.clientX - drag.startX) / rect.width : 0;
      const ns = snap(clamp(drag.startVal + deltaRatio * range, min, hi - windowSize));
      const ne = ns + windowSize;
      emit(ns, clamp(playhead, ns, ne), ne);
    }
  };

  const endDrag = (e) => {
    try { trackRef.current && trackRef.current.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
    dragRef.current = null;
  };

  const startPct = toPct(start);
  const endPct = toPct(end);
  const playheadPct = toPct(playhead);

  const grip = (side) => (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        [side]: '7px',
        transform: 'translateY(-50%)',
        width: '3px',
        height: '18px',
        borderRadius: '999px',
        bg: '#9B59B6',
        opacity: 0.9,
      }}
    />
  );

  return (
    <Box
      ref={trackRef}
      onPointerDown={onTrackPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      sx={{
        position: 'relative',
        width: '100%',
        height: '48px',
        // Own the gesture: no page scroll/zoom while dragging, works inside the modal's
        // scroll-lock, and no tap-highlight flash on mobile.
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        cursor: ready ? 'pointer' : 'default',
      }}
    >
      {/* Base timeline */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: '8px',
          transform: 'translateY(-50%)',
          bg: '#ECECEF',
          borderRadius: '999px',
        }}
      />

      {ready && (
        <>
          {/* Trim window — drag anywhere on it to move the 10s selection */}
          <Box
            onPointerDown={onWindowPointerDown}
            sx={{
              position: 'absolute',
              top: 0,
              height: '48px',
              left: `${startPct}%`,
              width: `${Math.max(0, endPct - startPct)}%`,
              minWidth: '24px',
              bg: 'rgba(155, 89, 182, 0.14)',
              border: '2px solid #9B59B6',
              borderRadius: '10px',
              boxSizing: 'border-box',
              touchAction: 'none',
              cursor: 'grab',
            }}
          >
            {grip('left')}
            {grip('right')}
          </Box>

          {/* Playhead — drag to scrub within the selection */}
          <Box
            onPointerDown={onPlayheadPointerDown}
            sx={{
              position: 'absolute',
              top: '-6px',
              height: '60px',
              width: '44px',
              marginLeft: '-22px',
              left: `${playheadPct}%`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              touchAction: 'none',
              cursor: 'ew-resize',
              zIndex: 2,
            }}
          >
            <Box sx={{ width: '4px', height: '100%', bg: '#6B2E8E', borderRadius: '999px' }} />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                bg: '#6B2E8E',
                boxShadow: 'medium',
                border: '2px solid #fff',
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default TrimSlider;
